import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth täytyy käyttää AuthProvider:n sisällä');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [elevatedChild, setElevatedChild] = useState(null);
  const [elevatedTimer, setElevatedTimer] = useState(null);
  const [elevationExpiresAt, setElevationExpiresAt] = useState(null);
  const kioskTokenRef = useRef(localStorage.getItem('kiosk_token_backup'));

  // Restore elevation state on mount
  useEffect(() => {
    const savedExpiresAt = localStorage.getItem('elevation_expires_at');
    const savedKioskToken = localStorage.getItem('kiosk_token_backup');
    const savedElevatedChild = localStorage.getItem('elevated_child');

    if (savedExpiresAt && savedKioskToken && savedElevatedChild) {
      const expiresAt = parseInt(savedExpiresAt, 10);
      const remaining = expiresAt - Date.now();

      if (remaining > 0) {
        // Restore elevation state
        kioskTokenRef.current = savedKioskToken;
        setElevatedChild(JSON.parse(savedElevatedChild));
        setElevationExpiresAt(expiresAt);
        const timer = setTimeout(() => {
          deElevateChildRef();
        }, remaining);
        setElevatedTimer(timer);
      } else {
        // Elevation expired, clean up
        clearElevationStorage();
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clearElevationStorage = () => {
    localStorage.removeItem('kiosk_token_backup');
    localStorage.removeItem('elevation_expires_at');
    localStorage.removeItem('elevated_child');
  };

  // Device authentication state
  const [deviceAuthenticated, setDeviceAuthenticated] = useState(false);
  const [deviceLoading, setDeviceLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [demoModeEnabled, setDemoModeEnabled] = useState(false);
  const [pwaEnabled, setPwaEnabled] = useState(true);
  const [devModeEnabled, setDevModeEnabled] = useState(false);
  const [noPasswordMode, setNoPasswordMode] = useState(false);

  const isKiosk = user?.username === 'kioski';
  const isElevated = !!elevatedChild;
  const isKioskContext = isKiosk || isElevated;

  // Verify device token on mount
  const verifyDevice = useCallback(async () => {
    const deviceToken = localStorage.getItem('family_device_token');

    try {
      // First check if setup is needed
      const setupResponse = await api.getSetupStatus();
      if (setupResponse.data?.needsSetup) {
        setNeedsSetup(true);
        setDeviceAuthenticated(false);
        setDeviceLoading(false);
        setLoading(false);
        return false;
      }
      if (setupResponse.data?.demoModeEnabled) {
        setDemoModeEnabled(true);
      }
      if (setupResponse.data?.pwaEnabled === false) {
        setPwaEnabled(false);
      }
      if (setupResponse.data?.devModeEnabled) {
        setDevModeEnabled(true);
      }

      const response = await api.verifyDevice();

      // If response indicates no password configured, skip device auth
      if (response.data?.noPasswordConfigured) {
        setDeviceAuthenticated(true);
        setNoPasswordMode(true);
        setDeviceLoading(false);
        return true;
      }

      if (!deviceToken) {
        setDeviceAuthenticated(false);
        setDeviceLoading(false);
        setLoading(false);
        return false;
      }

      setDeviceAuthenticated(true);
      return true;
    } catch (error) {
      // If 401 with DEVICE_AUTH_REQUIRED, need family login
      if (error.response?.data?.code === 'DEVICE_AUTH_REQUIRED') {
        localStorage.removeItem('family_device_token');
        setDeviceAuthenticated(false);
        setDeviceLoading(false);
        setLoading(false);
        return false;
      }
      // Other errors - assume device auth not required (bootstrapping)
      setDeviceAuthenticated(true);
      return true;
    } finally {
      setDeviceLoading(false);
    }
  }, []);

  const familyLogin = async (password) => {
    try {
      const deviceName = navigator.userAgent.substring(0, 100);
      const response = await api.familyLogin(password, deviceName);
      localStorage.setItem('family_device_token', response.data.deviceToken);
      setDeviceAuthenticated(true);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Kirjautuminen epäonnistui'
      };
    }
  };

  const familyLogout = async () => {
    try {
      await api.familyLogout();
    } catch (error) {
      console.error('Family logout error:', error);
    } finally {
      localStorage.removeItem('family_device_token');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('device_onboarded');
      setDeviceAuthenticated(false);
      setUser(null);
      setToken(null);
    }
  };

  // Listen for device auth required events
  useEffect(() => {
    const handleDeviceAuthRequired = () => {
      if (noPasswordMode) return;
      localStorage.removeItem('device_onboarded');
      setDeviceAuthenticated(false);
    };
    window.addEventListener('device-auth-required', handleDeviceAuthRequired);
    return () => window.removeEventListener('device-auth-required', handleDeviceAuthRequired);
  }, [noPasswordMode]);

  // Verify device on mount
  useEffect(() => {
    verifyDevice();
  }, [verifyDevice]);

  const loadPermissions = async () => {
    try {
      const res = await api.getPermissions();
      setPermissions(res.data);
    } catch (e) {
      console.error('Permissions load error:', e);
    } finally {
      setPermissionsLoaded(true);
    }
  };

  const canAccess = (path) => {
    // Public paths - always accessible
    const publicPaths = ['/', '/kauppa', '/profiili', '/kalenteri', '/uutiset'];
    if (publicPaths.includes(path)) return true;

    if (!permissionsLoaded) return false;
    if (!user) return false;
    if (isKiosk) return false;

    // Admin paths - parent only
    const parentPaths = ['/hallinta', '/tehtavat', '/asetukset'];
    if (parentPaths.includes(path)) return user.type === 'parent';

    // Check permission entry if exists
    const perm = permissions.find(p => p.path === path || path.startsWith(p.path + '/'));
    if (perm?.required_role === 'parent') return user.type === 'parent';
    if (perm?.required_role === 'child') return user.type === 'child' || user.type === 'parent';

    return false;
  };

  useEffect(() => {
    // Only check user auth after device is authenticated
    if (!deviceAuthenticated) {
      return;
    }

    if (token) {
      api.setAuthToken(token);
      const init = async () => {
        await Promise.all([checkAuthStatus(), loadPermissions()]);
        setLoading(false);
      };
      init();
    } else {
      setLoading(false);
      setPermissionsLoaded(true);
    }
  }, [token, deviceAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAuthStatus = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data.user);
    } catch (error) {
      console.error('Autentikointi epäonnistui:', error);
      logout();
    }
  };

  const login = async (username, password) => {
    try {
      const response = await api.post('/auth/login', { username, password });
      const { user: userData, token: authToken } = response.data;
      
      setUser(userData);
      setToken(authToken);
      localStorage.setItem('auth_token', authToken);
      api.setAuthToken(authToken);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Kirjautuminen epäonnistui:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Kirjautuminen epäonnistui' 
      };
    }
  };

const logout = async () => {
    if (isElevated) {
      deElevateChild();
      return;
    }
    try {
      if (token) {
        await api.post('/auth/logout');
      }
    } catch (error) {
      console.error('Uloskirjautuminen epäonnistui:', error);
    } finally {
      setUser(null);
      setToken(null);
      localStorage.removeItem('auth_token');
      api.setAuthToken(null);
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      return { success: true, user: response.data.user };
    } catch (error) {
      console.error('Rekisteröinti epäonnistui:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Rekisteröinti epäonnistui' 
      };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword });
      return { success: true };
    } catch (error) {
      console.error('Salasanan vaihto epäonnistui:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Salasanan vaihto epäonnistui' 
      };
    }
  };

  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data.user);
    } catch {}
  };

  const deElevateChildRef = () => {
    const saved = kioskTokenRef.current;
    if (saved) {
      localStorage.setItem('auth_token', saved);
      api.setAuthToken(saved);
      setToken(saved);
    }
    setElevatedChild(null);
    setElevatedTimer(null);
    setElevationExpiresAt(null);
    clearElevationStorage();
    refreshUser();
  };

  const elevateChild = (childUser, childToken) => {
    clearTimeout(elevatedTimer);
    const currentToken = localStorage.getItem('auth_token');
    kioskTokenRef.current = currentToken;

    // Persist elevation state to localStorage
    localStorage.setItem('kiosk_token_backup', currentToken);
    localStorage.setItem('auth_token', childToken);
    api.setAuthToken(childToken);
    setUser(childUser);
    setElevatedChild(childUser);

    const expiresAt = Date.now() + 5 * 60 * 1000;
    setElevationExpiresAt(expiresAt);
    localStorage.setItem('elevation_expires_at', expiresAt.toString());
    localStorage.setItem('elevated_child', JSON.stringify(childUser));

    const timer = setTimeout(() => {
      deElevateChildRef();
    }, 5 * 60 * 1000);
    setElevatedTimer(timer);
    return childUser;
  };

  const deElevateChild = () => {
    clearTimeout(elevatedTimer);
    deElevateChildRef();
  };

  const value = {
    user,
    loading,
    login,
    logout,
    register,
    changePassword,
    refreshUser,
    permissions,
    permissionsLoaded,
    canAccess,
    isKiosk,
    isKioskContext,
    isElevated,
    elevatedChild,
    elevateChild,
    deElevateChild,
    elevationExpiresAt,
    loadPermissions,
    // Device auth
    deviceAuthenticated,
    deviceLoading,
    familyLogin,
    familyLogout,
    verifyDevice,
    needsSetup,
    setNeedsSetup,
    demoModeEnabled,
    pwaEnabled,
    devModeEnabled,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};